import { useEffect } from 'react';

import FormTitle from 'components/PageLayout/FormTitle';
import { WithBackButton } from 'components/curo-components/common/BackButton';
import Typography from 'components/curo-components/common/Typography';
import ButtonWithLoadingState from 'components/digifi-wrappers/ButtonWithLoadingState';
import { GoogleAnalyticsEventType } from 'enums/GoogleAnalyticsEventType';
import { CuroApplicationVariables, BankAccountDataVariables } from 'enums/curo/Variables';
import { getApplicationSelector } from 'handlers/curo-handlers/selectors';
import { useAppSelector } from 'hooks/reduxHooks';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import useGoogleAnalytics from 'hooks/useGoogleAnalytics';
import { TableValue } from 'product_modules/api/Types';
import media from 'styles/media.module.scss';
import { saveAutoPayOption } from 'thunks';
import styled from 'types/AppTheme';

const PageDescriptionWrapper = styled.div`
  max-width: 690px;
  margin-bottom: 36px;
`;

const TableWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  margin-bottom: 28px;
  column-gap: 30px;

  & > div {
    border-top: 1px solid #e7e9ec;
    border-bottom: 1px solid #e7e9ec;
  }

  & > div:last-child {
    border-top: none;
  }

  @media (max-width: ${media.maxMobile}) {
    grid-template-columns: 1fr;
    grid-template-rows: auto;

    & > div {
      border-top: 1px solid #e7e9ec;
      border-bottom: none;
    }

    & > div:last-child {
      border-top: 1px solid #e7e9ec;
      border-bottom: 1px solid #e7e9ec;
    }
  }
`;

const TableRow = styled.div<{ capitalizeValue: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 14px 10px;
  column-gap: 20px;

  & > p:first-child {
    color: #9fa7b3;
    text-transform: ${({ capitalizeValue }) => (capitalizeValue ? 'capitalize' : 'none')};
  }
  & > p:last-child {
    color: #3f4f68;
    text-transform: ${({ capitalizeValue }) => (capitalizeValue ? 'capitalize' : 'none')};
  }
`;

const PAGE_TITLE = 'Set up AutoPay';

interface ISetupAutoPayStepProps {
  onBackButtonClick: () => void;
}

const SetupAutoPayStep = ({ onBackButtonClick }: ISetupAutoPayStepProps) => {
  const application = useAppSelector(getApplicationSelector)!;
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const trackEvent = useGoogleAnalytics();

  useEffect(() => {
    trackEvent(GoogleAnalyticsEventType.ApplyProcessAutoPaySetup);
  }, []);

  const handleContinueClick = async () => {
    await dispatchWithUnwrap(
      saveAutoPayOption({
        autoPay: true,
      }),
    );
    trackEvent(GoogleAnalyticsEventType.AutopayDecision);
  };

  const isBankAccountDataExists = application.variables[
    CuroApplicationVariables.BankAccountData
  ] as TableValue;
  const selectedBankAccountData =
    isBankAccountDataExists &&
    (application.variables[CuroApplicationVariables.BankAccountData] as TableValue).find(
      (account) => account.selected,
    );

  const bankAccountType = selectedBankAccountData
    ? selectedBankAccountData[BankAccountDataVariables.BankAccountType]
    : application.variables[CuroApplicationVariables.BankAccountType];

  const routingNumber = selectedBankAccountData
    ? selectedBankAccountData[BankAccountDataVariables.BankRoutingNumber]
    : application.variables[CuroApplicationVariables.BankRoutingNumber];

  const accountNumber = selectedBankAccountData
    ? `******${selectedBankAccountData[BankAccountDataVariables.AccountMask]}`
    : application.variables[CuroApplicationVariables.BankAccountNumberMasked];

  const dataToRender = [
    {
      label: 'Bank Account Type',
      value: bankAccountType,
      capitalizeValue: true,
    },

    {
      label: 'Routing Number',
      value: routingNumber,
    },
    {
      label: 'Account Number',
      value: accountNumber,
    },
  ];

  return (
    <>
      <FormTitle
        title={PAGE_TITLE}
        subTitle={
          <PageDescriptionWrapper>
            <Typography>
              Your payment amount will be deducted from your bank account starting on your first
              payment due date. See your loan agreement documents for more details.
            </Typography>
            <br />

            <Typography>
              AutoPay will be set with the bank account information provided below:
            </Typography>
          </PageDescriptionWrapper>
        }
      />
      <TableWrapper>
        {dataToRender.map(({ label, value, capitalizeValue }) => {
          return (
            <TableRow key={value + label} capitalizeValue={!!capitalizeValue}>
              <Typography type="secondary">{label}</Typography>
              <Typography type="secondary">{value}</Typography>
            </TableRow>
          );
        })}
      </TableWrapper>

      <WithBackButton onBackButtonClick={onBackButtonClick}>
        <ButtonWithLoadingState
          type="submit"
          kind="primary"
          size="form"
          onClick={handleContinueClick}
        >
          Continue
        </ButtonWithLoadingState>
      </WithBackButton>
    </>
  );
};

export default SetupAutoPayStep;
